import { useEffect, useMemo, useState } from 'react'
import Pagination from '@/shared/components/Pagination/Pagination'
import { Button } from '@/ui/button'
import { MaterialIcon } from '@/ui/material-icon'
import { Input } from '@/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/ui/table'
import styles from './RunHistoryPanel.module.scss'

export type RunHistoryPanelProps = {
  onClose: () => void
}

type RunStatus = 'finished' | 'stopped'

type RunRow = {
  id: string
  activityName: string
  user: string
  status: RunStatus
  startTime: string
  stopTime: string
}

const MOCK_RUNS: RunRow[] = Array.from({ length: 100 }, (_, i) => ({
  id: `run-${i}`,
  activityName: 'Untitled_Pipeline_Run',
  user: 'Lorem ipsum',
  status: i % 3 === 0 ? 'stopped' : 'finished',
  startTime: '02/20/2026, 3:29 PM',
  stopTime: '02/20/2026, 3:45 PM',
}))

const PAGE_SIZE_OPTIONS = ['10', '25', '50'] as const

export function RunHistoryPanel({ onClose }: RunHistoryPanelProps) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>('10')
  const [runCycle, setRunCycle] = useState('feb-2026')

  const size = Number.parseInt(pageSize, 10)
  const totalPages = Math.max(1, Math.ceil(MOCK_RUNS.length / size))

  useEffect(() => {
    setPage(p => Math.min(p, totalPages))
  }, [totalPages])

  const pageRows = useMemo(() => {
    const start = (page - 1) * size
    return MOCK_RUNS.slice(start, start + size)
  }, [page, size])

  const displayPage = Math.min(page, totalPages)

  return (
    <aside className={styles.root} aria-label="Pipeline run history">
      <div className={styles.inner}>
        <header className={styles.pageHeader}>
          <div className={styles.headRow}>
            <div className={styles.headLeft}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={styles.closeChevron}
                onClick={onClose}
                aria-label="Close run history"
              >
                <span aria-hidden>&gt;&gt;</span>
              </Button>
              <div className={styles.titleBlock}>
                <h1 className={styles.title}>Run History</h1>
                <p className={styles.subtitle}>
                  View and track the status of the activities for the pipeline/job
                </p>
              </div>
            </div>
            <div className={styles.toolbar}>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={styles.iconCircleBtn}
                aria-label="Refresh run history"
                onClick={() => {}}
              >
                <MaterialIcon name="refresh" size={20} />
              </Button>
              <div className={styles.searchWrap}>
                <span className={styles.searchIcon} aria-hidden>
                  <MaterialIcon name="search" size={18} />
                </span>
                <Input
                  type="search"
                  placeholder="Search by keyword"
                  className={styles.searchInput}
                  aria-label="Search run history by keyword"
                />
              </div>
              <Button type="button" variant="outline" className={styles.filtersBtn}>
                <MaterialIcon name="add" size={18} />
                Filters
              </Button>
              <Select value={runCycle} onValueChange={setRunCycle}>
                <SelectTrigger className={styles.cycleSelect} aria-label="Run cycle date range">
                  <SelectValue placeholder="Run cycle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="feb-2026">Run Cycle (Feb 01 – Feb 28)</SelectItem>
                  <SelectItem value="jan-2026">Run Cycle (Jan 01 – Jan 31)</SelectItem>
                  <SelectItem value="mar-2026">Run Cycle (Mar 01 – Mar 31)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </header>

        <div className={styles.tableScroll}>
          <Table className={styles.dataTable}>
            <TableHeader>
              <TableRow className={styles.headerRow}>
                <TableHead className={styles.colActivity}>Activity Name</TableHead>
                <TableHead className={styles.colUser}>User</TableHead>
                <TableHead className={styles.colStatus}>Status</TableHead>
                <TableHead className={styles.colTime}>Start Time</TableHead>
                <TableHead className={styles.colTime}>Stop Time</TableHead>
                <TableHead className={styles.colAction}>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRows.map((row, idx) => (
                <TableRow
                  key={row.id}
                  className={idx % 2 === 1 ? styles.rowAlt : undefined}
                >
                  <TableCell className={styles.cellStrong}>{row.activityName}</TableCell>
                  <TableCell>{row.user}</TableCell>
                  <TableCell>
                    {row.status === 'finished' ? (
                      <span className={`${styles.statusPill} ${styles.statusFinished}`}>
                        <MaterialIcon name="check" size={14} />
                        Finished
                      </span>
                    ) : (
                      <span className={`${styles.statusPill} ${styles.statusStopped}`}>
                        <MaterialIcon name="stop" size={14} />
                        Stopped
                      </span>
                    )}
                  </TableCell>
                  <TableCell className={styles.cellMuted}>{row.startTime}</TableCell>
                  <TableCell className={styles.cellMuted}>{row.stopTime}</TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={styles.viewBtn}
                      aria-label="View run details"
                    >
                      <MaterialIcon name="visibility" size={20} />
                      <span>View</span>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className={styles.footer}>
          <Pagination
            currentPage={displayPage}
            totalPages={totalPages}
            rowsPerPage={size}
            onPageChange={setPage}
            onRowsPerPageChange={n => {
              setPageSize(String(n) as (typeof PAGE_SIZE_OPTIONS)[number])
              setPage(1)
            }}
            pageSizeOptions={[10, 25, 50]}
          />
        </div>
      </div>
    </aside>
  )
}
